import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { collection, doc, addDoc, setDoc, getDoc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import './ProUserChat.css';

const ProUserChat = () => {
    const { user } = useContext(AuthContext);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const [isSending, setIsSending] = useState(false);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
    const messagesContainerRef = useRef(null);
    
    // Initialize or get the chat document
    useEffect(() => {
        if (!user?.uid) return;
        
        const initializeChat = async () => {
            const chatDocRef = doc(db, 'pro_chats', user.uid);
            
            try {
                const chatDoc = await getDoc(chatDocRef);
                
                if (!chatDoc.exists()) {
                    await setDoc(chatDocRef, {
                        userId: user.uid,
                        userName: user.displayName || 'Anonymous User',
                        lastMessageTime: serverTimestamp(),
                        unreadByAdmin: 0,
                        unreadByUser: 0,
                        adminViewing: false
                    });
                } else {
                    // Reset the adminViewing flag when user opens their chat
                    await setDoc(chatDocRef, {
                        adminViewing: false
                    }, { merge: true });
                }
                
                setLoading(false);
            } catch (error) {
                console.error("Error initializing chat:", error);
                setLoading(false);
            }
        };
        
        initializeChat();
    }, [user]);
    
    // Listen for messages in real-time
    useEffect(() => {
        if (!user?.uid || loading) return;
        
        const messagesRef = collection(db, 'pro_chats', user.uid, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messageList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(messageList);
            
            // Mark messages from admin as read
            snapshot.docChanges().forEach(change => {
                if (change.type === 'added' && 
                    change.doc.data().senderId !== user.uid && 
                    !change.doc.data().read) {
                    setDoc(doc(db, 'pro_chats', user.uid, 'messages', change.doc.id), 
                        { read: true }, 
                        { merge: true }
                    );
                }
            });
            
            // Update unreadByUser count to 0 when user views the chat
            setDoc(doc(db, 'pro_chats', user.uid), 
                { unreadByUser: 0 }, 
                { merge: true }
            );
        });
        
        return () => unsubscribe();
    }, [user, loading]);
    
    // Handle scroll events to detect if user scrolled up
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        
        const handleScroll = () => {
            // Check if user is at bottom (within 100px threshold)
            const threshold = 100;
            const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
            setShouldAutoScroll(isAtBottom);
        };
        
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);
    
    // Auto-scroll to bottom when new messages arrive (only if user is at bottom)
    useEffect(() => {
        if (messages.length > 0 && shouldAutoScroll) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [messages, shouldAutoScroll]);
    
    // When user sends a message, always scroll to bottom and enable auto-scroll
    useEffect(() => {
        if (isSending && messages.length > 0) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                setShouldAutoScroll(true);
            }, 100);
        }
    }, [isSending, messages.length]);
    
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !user?.uid || isSending) return;
        
        setIsSending(true);
        
        try {
            // Add the new message
            const messageData = {
                text: newMessage,
                senderId: user.uid,
                timestamp: serverTimestamp(),
                read: false
            };
            
            try {
                await addDoc(collection(db, 'pro_chats', user.uid, 'messages'), messageData);
                
                // Create a notification document to trigger the email
                await addDoc(collection(db, 'email_notifications'), {
                    type: 'new_message',
                    userId: user.uid,
                    userName: user.displayName || 'Anonymous User',
                    userEmail: user.email || 'Unknown Email',
                    messageText: newMessage,
                    timestamp: serverTimestamp(),
                    processed: false
                });
                
            } catch (error) {
                console.error("Error adding message:", error);
            }
            
            // Update the chat document
            try {
                await setDoc(doc(db, 'pro_chats', user.uid), {
                    lastMessageTime: serverTimestamp(),
                    unreadByAdmin: (messages.filter(m => m.senderId === user.uid && !m.read).length + 1)
                }, { merge: true });
            } catch (error) {
                console.error("Error updating chat:", error);
            }
            
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setIsSending(false);
        }
    };
    
    if (loading) {
        return (
            <div className="pro-user-chat">
                <h2>Pro User Chat</h2>
                <div className="chat-content loading">
                    <p>Loading chat...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="pro-user-chat">
            <h2>Pro User Chat</h2>
            <div className="chat-messages" ref={messagesContainerRef}>
                {messages.length === 0 ? (
                    <div className="empty-chat">
                        <p>No messages yet. Start a conversation with the admin!</p>
                    </div>
                ) : (
                    messages.map(message => (
                        <div 
                            key={message.id} 
                            className={`message ${message.senderId === user.uid ? 'user' : 'admin'}`}
                        >
                            <div className="message-content">
                                <p>{message.text}</p>
                                <span className="message-time">
                                    {message.timestamp ? new Date(message.timestamp.toDate()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Sending...'}
                                </span>
                            </div>
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>
            
            <form className="chat-input-form" onSubmit={handleSendMessage}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message here..."
                    disabled={isSending}
                />
                <button type="submit" disabled={!newMessage.trim() || isSending}>
                    {isSending ? 'Sending...' : 'Send'}
                </button>
            </form>
        </div>
    );
};

export default ProUserChat; 